import { computed, getCurrentScope, onScopeDispose, ref, type ComputedRef, type Ref } from 'vue'

import {
  entityMappingApi,
  type EntityMappingApi,
  type EntityMappingError,
  normalizeEntityMappingError,
} from '../api/EntityMappingApi'
import { SaveEntityMappingFlow } from '../flows/SaveEntityMappingFlow'
import type {
  EntityMappingDescriptor,
  EntityMappingDraft,
  EntityMappingOption,
  EntityMappingState,
  SaveEntityMappingOutcome,
  UnmappedEntity,
} from '../models/entityMapping.types'
import {
  availableEntityMappingOptions,
  buildEntityMappingDraft,
  defaultEntityMappingChannel,
  entityMappingChannels,
} from '../service/entityMapping'

import { useWorkspace } from '@/features/account'
import { useReportSchedules } from '@/features/reports'

const INITIAL_ROWS = 20
const ROW_INCREMENT = 200

export interface UseEntityMappingOptions {
  api?: EntityMappingApi
  flow?: SaveEntityMappingFlow
  workspaceId?: Ref<number | string | undefined>
  scheduleId?: () => number | string | null
}

export interface UseEntityMapping {
  descriptor: EntityMappingDescriptor
  unmapped: Ref<UnmappedEntity[]>
  channels: Ref<string[]>
  selectedChannel: Ref<string | null>
  draft: Ref<EntityMappingDraft>
  query: Ref<string>
  state: Ref<EntityMappingState>
  pending: Ref<boolean>
  error: Ref<EntityMappingError | null>
  incomplete: Ref<string[]>
  saveOutcome: Ref<SaveEntityMappingOutcome | null>
  visibleEntities: ComputedRef<UnmappedEntity[]>
  canSave: ComputedRef<boolean>
  load: () => Promise<void>
  save: () => Promise<SaveEntityMappingOutcome | null>
  resetDraft: () => void
  loadMore: () => void
  selectChannel: (channel: string) => void
  setQuery: (value: string | number | undefined) => void
  entityId: (entity: UnmappedEntity) => string
  optionsFor: (entityId: string, draftKey: string) => string[]
  setValues: (entityId: string, draftKey: string, values: string[]) => void
}

export function useEntityMapping(
  descriptor: EntityMappingDescriptor,
  options: UseEntityMappingOptions = {},
): UseEntityMapping {
  const api = options.api ?? entityMappingApi
  const flow = options.flow ?? new SaveEntityMappingFlow(api)
  const workspace = options.workspaceId ? null : useWorkspace()
  const schedules = options.scheduleId ? null : useReportSchedules()
  const unmapped = ref<UnmappedEntity[]>([])
  const channels = ref<string[]>([])
  const selectedChannel = ref<string | null>(null)
  const rawOptions = ref<EntityMappingOption[]>([])
  const draft = ref<EntityMappingDraft>({})
  const query = ref('')
  const rowLimit = ref(INITIAL_ROWS)
  const state = ref<EntityMappingState>('loading')
  const pending = ref(false)
  const error = ref<EntityMappingError | null>(null)
  const incomplete = ref<string[]>([])
  const saveOutcome = ref<SaveEntityMappingOutcome | null>(null)
  let controller: AbortController | null = null

  const currentWorkspaceId = (): number | string | undefined =>
    options.workspaceId?.value ?? workspace?.workspaceId.value
  const currentScheduleId = (): number | string | null =>
    options.scheduleId?.() ??
    schedules?.getScheduleByReportId(descriptor.scheduleReportId)?.id ??
    null

  function entityId(entity: UnmappedEntity): string {
    const value = entity[descriptor.idKey]
    return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  }

  function resetDraft(): void {
    draft.value = buildEntityMappingDraft(descriptor, unmapped.value)
    incomplete.value = []
    saveOutcome.value = null
  }

  async function load(): Promise<void> {
    controller?.abort()
    controller = null
    const workspaceId = currentWorkspaceId()
    const scheduleId = currentScheduleId()
    error.value = null
    incomplete.value = []
    saveOutcome.value = null
    query.value = ''
    rowLimit.value = INITIAL_ROWS
    unmapped.value = []
    channels.value = []
    rawOptions.value = []
    draft.value = {}
    if (workspaceId === undefined || scheduleId === null) {
      state.value = 'no-schedule'
      return
    }
    state.value = 'loading'
    const requestController = new AbortController()
    controller = requestController
    try {
      const response = await api.get(
        descriptor.path(workspaceId),
        scheduleId,
        requestController.signal,
      )
      const entities = response[descriptor.unmappedKey]
      const optionsPayload = response[descriptor.optionsCollectionKey]
      unmapped.value = Array.isArray(entities) ? (entities as UnmappedEntity[]) : []
      rawOptions.value = Array.isArray(optionsPayload)
        ? (optionsPayload as EntityMappingOption[])
        : []
      channels.value = entityMappingChannels(unmapped.value)
      selectedChannel.value = defaultEntityMappingChannel(channels.value)
      resetDraft()
      state.value = unmapped.value.length ? 'ready' : 'empty'
    } catch (cause) {
      const classified = normalizeEntityMappingError(cause)
      if (classified.kind === 'canceled') return
      error.value = classified
      state.value = 'error'
    } finally {
      if (controller === requestController) controller = null
    }
  }

  async function save(): Promise<SaveEntityMappingOutcome | null> {
    const workspaceId = currentWorkspaceId()
    if (workspaceId === undefined) return null
    pending.value = true
    error.value = null
    incomplete.value = []
    saveOutcome.value = null
    try {
      const outcome = await flow.save(descriptor, draft.value, workspaceId)
      saveOutcome.value = outcome
      if (outcome.kind === 'incomplete') incomplete.value = outcome.entityIds
      if (outcome.kind === 'saved') await load()
      return outcome
    } catch (cause) {
      error.value = normalizeEntityMappingError(cause)
      return null
    } finally {
      pending.value = false
    }
  }

  const filteredEntities = computed(() => {
    const needle = query.value.trim().toLocaleLowerCase()
    return unmapped.value.filter((entity) => {
      if (selectedChannel.value && entity.marketing_channel !== selectedChannel.value) return false
      if (!needle) return true
      const primary = entity[descriptor.nameKey]
      const context = descriptor.contextNameKey ? entity[descriptor.contextNameKey] : null
      return [primary, context].some(
        (value) => typeof value === 'string' && value.toLocaleLowerCase().includes(needle),
      )
    })
  })

  if (getCurrentScope()) onScopeDispose(() => controller?.abort())

  return {
    descriptor,
    unmapped,
    channels,
    selectedChannel,
    draft,
    query,
    state,
    pending,
    error,
    incomplete,
    saveOutcome,
    visibleEntities: computed(() => filteredEntities.value.slice(0, rowLimit.value)),
    canSave: computed(() =>
      Object.values(draft.value).some((row) => Object.values(row).some((values) => values.length)),
    ),
    load,
    save,
    resetDraft,
    loadMore: () => {
      if (rowLimit.value < filteredEntities.value.length) rowLimit.value += ROW_INCREMENT
    },
    selectChannel: (channel) => {
      selectedChannel.value = channel
    },
    setQuery: (value) => {
      query.value = String(value ?? '')
    },
    entityId,
    optionsFor: (id, draftKey) =>
      availableEntityMappingOptions(descriptor, draftKey, draft.value[id] ?? {}, rawOptions.value),
    setValues: (id, draftKey, values) => {
      const row = draft.value[id]
      if (row) row[draftKey] = values
    },
  }
}
